import { sha1, toHex } from "./hash.js";

/** RFC 4122 URL namespace — the namespace needle-cloud's `ensureUUID` uses (`uuidv5.URL`). */
export const UUID_NAMESPACE_URL = "6ba7b811-9dad-11d1-80b4-00c04fd430c8";

/**
 * RFC 4122 version-5 (SHA-1, name-based) UUID. Byte-identical to the `uuid` npm
 * package's `v5(name, namespace)`.
 */
export function uuidv5(name: string, namespace: string = UUID_NAMESPACE_URL): string {
    const ns = parseUuidBytes(namespace);
    const nameBytes = new TextEncoder().encode(name);
    const input = new Uint8Array(ns.length + nameBytes.length);
    input.set(ns);
    input.set(nameBytes, ns.length);
    const hash = sha1(input).slice(0, 16);
    hash[6] = (hash[6] & 0x0f) | 0x50; // version 5
    hash[8] = (hash[8] & 0x3f) | 0x80; // RFC 4122 variant
    return formatUuid(hash);
}

/**
 * Random UUID for content that has no source file to be deterministic against
 * (scratch content created in an editor). Uses the platform's crypto when available.
 */
export function randomUuid(): string {
    const cryptoObj = globalThis.crypto;
    if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
    const bytes = new Uint8Array(16);
    if (cryptoObj?.getRandomValues) cryptoObj.getRandomValues(bytes);
    else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    return formatUuid(bytes);
}

function parseUuidBytes(uuid: string): Uint8Array {
    const hex = uuid.replace(/-/g, "");
    if (hex.length !== 32) throw new Error(`Invalid UUID: ${uuid}`);
    const bytes = new Uint8Array(16);
    for (let i = 0; i < 16; i++) bytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
    return bytes;
}

function formatUuid(bytes: Uint8Array): string {
    const hex = toHex(bytes);
    return `${hex.substring(0, 8)}-${hex.substring(8, 12)}-${hex.substring(12, 16)}-${hex.substring(16, 20)}-${hex.substring(20)}`;
}
